import ApiService from './apiService';

/**
 * Service pour gérer les transactions d'inventaire
 */
class InventoryTransactionService {
    /**
     * Récupérer la liste des transactions avec filtres et pagination
     * @param {Object} params Paramètres de filtrage et pagination
     * @returns {Promise} Réponse de l'API avec la liste des transactions
     */
    getTransactions(params = {}) {
        return ApiService.get('/inventory-transactions', params);
    }

    /**
     * Récupérer une transaction par son ID
     * @param {Number} id ID de la transaction
     * @returns {Promise} Réponse de l'API avec les détails de la transaction
     */
    getTransaction(id) {
        return ApiService.get(`/inventory-transactions/${id}`);
    }

    /**
     * Créer une nouvelle transaction d'inventaire
     * @param {Object} transactionData Données de la transaction
     * @returns {Promise} Réponse de l'API avec la transaction créée
     */
    createTransaction(transactionData) {
        return ApiService.post('/inventory-transactions', transactionData);
    }

    /**
     * Mettre à jour une transaction existante
     * @param {Number} id ID de la transaction à mettre à jour
     * @param {Object} transactionData Nouvelles données de la transaction
     * @returns {Promise} Réponse de l'API avec la transaction mise à jour
     */
    updateTransaction(id, transactionData) {
        return ApiService.put(`/inventory-transactions/${id}`, transactionData);
    }

    /**
     * Supprimer une transaction
     * @param {Number} id ID de la transaction à supprimer
     * @returns {Promise} Réponse de l'API
     */
    deleteTransaction(id) {
        return ApiService.delete(`/inventory-transactions/${id}`);
    }

    /**
     * Récupérer les statistiques des transactions
     * @param {Object} params Paramètres de filtrage (start_date, end_date)
     * @returns {Promise} Réponse de l'API avec les statistiques
     */
    getStats(params = {}) {
        return ApiService.get('/inventory-transactions/stats', params);
    }

    /**
     * Exporter les transactions en CSV
     * @param {Object} params Paramètres de filtrage
     * @returns {Promise} Fichier CSV téléchargeable
     */
    exportTransactions(params = {}) {
        return ApiService.get('/inventory-transactions/export', params, {
            responseType: 'blob'
        });
    }

    /**
     * Récupérer les transactions d'un produit spécifique
     * @param {Number} productId ID du produit
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les transactions du produit
     */
    getProductTransactions(productId, params = {}) {
        return ApiService.get(`/inventory/products/${productId}/transactions`, params);
    }

    /**
     * Ajuster le stock d'un produit (créer une transaction)
     * @param {Number} productId ID du produit
     * @param {Object} adjustmentData Données d'ajustement
     * @returns {Promise} Réponse de l'API
     */
    adjustProductStock(productId, adjustmentData) {
        return ApiService.post(`/inventory/products/${productId}/adjust`, adjustmentData);
    }

    /**
     * Obtenir le libellé du type de transaction en français
     * @param {String} type Type de transaction
     * @returns {String} Libellé traduit
     */
    getTransactionTypeLabel(type) {
        const labels = {
            'purchase': 'Achat',
            'sale': 'Vente',
            'adjustment': 'Ajustement',
            'return': 'Retour',
            'damage': 'Perte/Dommage',
            'transfer': 'Transfert'
        };
        return labels[type] || type;
    }

    /**
     * Obtenir la couleur associée au type de transaction
     * @param {String} type Type de transaction
     * @returns {String} Classe CSS ou code couleur
     */
    getTransactionTypeColor(type) {
        const colors = {
            'purchase': 'success',      // Vert
            'sale': 'primary',          // Bleu
            'adjustment': 'warning',     // Jaune/Orange
            'return': 'info',           // Bleu clair
            'damage': 'danger',         // Rouge
            'transfer': 'secondary'     // Gris/Violet
        };
        return colors[type] || 'secondary';
    }

    /**
     * Formater une transaction pour l'affichage
     * @param {Object} transaction Données de la transaction
     * @returns {Object} Transaction formatée
     */
    formatTransaction(transaction) {
        if (!transaction) return null;

        return {
            ...transaction,
            type_label: this.getTransactionTypeLabel(transaction.transaction_type),
            type_color: this.getTransactionTypeColor(transaction.transaction_type),
            quantity_display: transaction.quantity_change >= 0 
                ? `+${transaction.quantity_change}` 
                : transaction.quantity_change,
            quantity_class: transaction.quantity_change >= 0 
                ? 'text-success' 
                : 'text-danger'
        };
    }

    /**
     * Filtrer les transactions par période
     * @param {Array} transactions Liste des transactions
     * @param {String} startDate Date de début (format ISO)
     * @param {String} endDate Date de fin (format ISO)
     * @returns {Array} Transactions filtrées
     */
    filterByDateRange(transactions, startDate, endDate) {
        if (!transactions || !transactions.length) return [];

        const start = new Date(startDate);
        const end = new Date(endDate);

        return transactions.filter(transaction => {
            const transactionDate = new Date(transaction.transaction_date);
            return transactionDate >= start && transactionDate <= end;
        });
    }

    /**
     * Grouper les transactions par type
     * @param {Array} transactions Liste des transactions
     * @returns {Object} Transactions groupées par type
     */
    groupByType(transactions) {
        if (!transactions || !transactions.length) return {};

        return transactions.reduce((groups, transaction) => {
            const type = transaction.transaction_type;
            if (!groups[type]) {
                groups[type] = [];
            }
            groups[type].push(transaction);
            return groups;
        }, {});
    }

    /**
     * Calculer le total des mouvements
     * @param {Array} transactions Liste des transactions
     * @returns {Object} Totaux (entrées, sorties, net)
     */
    calculateTotals(transactions) {
        if (!transactions || !transactions.length) {
            return {
                totalIn: 0,
                totalOut: 0,
                net: 0,
                count: 0
            };
        }

        const totals = transactions.reduce((acc, transaction) => {
            const change = parseFloat(transaction.quantity_change) || 0;
            if (change > 0) {
                acc.totalIn += change;
            } else {
                acc.totalOut += Math.abs(change);
            }
            return acc;
        }, { totalIn: 0, totalOut: 0 });

        return {
            ...totals,
            net: totals.totalIn - totals.totalOut,
            count: transactions.length
        };
    }
}

export default new InventoryTransactionService();

